import React, { FC, useContext, useState } from "react";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { MobXProviderContext } from "mobx-react";
import { Controller, useForm } from "react-hook-form";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toast } from "primereact/toast";
import { Checkbox } from "primereact/checkbox";
import { getToastMessageForSubscriptionLimitReached } from "../../../constants/constants";
import { RouterAddress } from "../../../constants/RouterAddress";
import {
  appNeutralPanelStyle,
  appAccentPanelStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  renderDialogFooter,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";
import { useTour } from "../../../shared/tour/TourProvider";
import { useNavigate } from "react-router-dom";
import { ResidentProfile } from "../../../models/residentProfile";

interface AddResidentModalProps {
  homeId: string | undefined;
  facilityType?: string | null;
  isOpen: boolean;
  toggleModal: (open: boolean) => void;
  flag: boolean;
  setFlag: (flag: boolean) => void;
  onAdded?: (resident: ResidentProfile) => Promise<void> | void;
}
const AddResidentModal: FC<AddResidentModalProps> = ({
  homeId,
  facilityType,
  isOpen,
  toggleModal,
  flag,
  setFlag,
  onAdded,
}) => {
  const toast = React.useRef<Toast>(null);
  const { backendAPIStore } = useContext(MobXProviderContext);
  const { completeStep, isStepActive } = useTour();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [showResidentUnlockDialog, setShowResidentUnlockDialog] = useState<boolean>(false);
  const [currentResidentLimit, setCurrentResidentLimit] = useState<number>(0);
  const [showNextStepDialog, setShowNextStepDialog] = useState<boolean>(false);
  const [createdResident, setCreatedResident] = useState<ResidentProfile | null>(null);

  const defaultValues = {
    firstName: '',
    lastName: '',
    isOnMedicaid: false,
  }

  const { control, formState: { errors }, handleSubmit, reset } = useForm({ defaultValues });

  const onSubmit = async (data: any) => {
    setErrorMessage(null);
    setLoading(true);
    try {
      data.homeId = homeId;
      await backendAPIStore.init();
      const response = await backendAPIStore.addResident(data);
      const nextCreatedResident = {
        ...data,
        residentId: response?.residentId,
        homeId,
      } as ResidentProfile;
      if (isStepActive("save-resident")) {
        completeStep("save-resident");
      }
      reset();
      toggleModal(false);
      setFlag(!flag);
      setCreatedResident(nextCreatedResident);
      await onAdded?.(nextCreatedResident);
      setShowNextStepDialog(true);
    } catch (error: any) {
      setErrorMessage(error.message);
      const subscriptionLimitDetails = error?.details?.details || error?.details || {};
      const reachedResidentLimit =
        error?.code === "SUBSCRIPTION_LIMIT_REACHED"
        || error?.cause?.message === "SUBSCRIPTION_LIMIT_REACHED"
        || subscriptionLimitDetails?.canPurchaseResidentUnlock;

      if (reachedResidentLimit) {
        setCurrentResidentLimit(Number(
          subscriptionLimitDetails?.allowedResidents
          || subscriptionLimitDetails?.purchasedAllowedResidents
          || 0
        ));
        setShowResidentUnlockDialog(true);
      } else {
        toast.current?.show(getToastMessageForSubscriptionLimitReached(error));
      }
    } finally {
      setLoading(false);
    }
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  const headerElement = renderSurfaceHeader(
    "Add Resident",
    "Create a resident record to attach to this home.",
    "pi pi-user-plus",
  );

  const footerContent = renderDialogFooter({
    errorMessage,
    loading,
    onCancel: () => toggleModal(false),
    onSubmit: handleSubmit(onSubmit),
    submitLabel: "Save Resident",
    submitButtonProps: {
      "data-tour": "provider-save-resident",
    },
  });

  return (
    <>
      <Dialog visible={isOpen} modal header={headerElement} footer={footerContent} style={appDialogStyle}
        headerStyle={appDialogHeaderStyle} contentStyle={appDialogContentStyle}
        onHide={() => { if (!isOpen) return; toggleModal(false); }}>
        <Toast ref={toast} />
        <form>
          <div style={appAccentPanelStyle} className="mb-3">
            <div style={{ fontWeight: 700, color: "#9a3412" }}>Resident basics</div>
            <div className="mt-2" style={appMutedTextStyle}>
              Start with the resident's name. You can update additional details later from the resident profile.
            </div>
          </div>
          <div style={appFormSectionStyle}>
            <div className="field">
              <span className="p-float-label">
                <Controller name="firstName" control={control} rules={{
                  required: 'Name is required.',
                  maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="firstName" className={classNames({ 'p-error': errors.firstName })}>First Name</label>
              </span>
              {getFormErrorMessage('firstName')}
            </div>
            <div className="field">
              <span className="p-float-label">
                <Controller name="lastName" control={control} rules={{
                  required: 'Name is required.',
                  maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
                }} render={({ field, fieldState }) => (
                  <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                )} />
                <label htmlFor="lastName" className={classNames({ 'p-error': errors.lastName })}>Last Name</label>
              </span>
              {getFormErrorMessage('lastName')}
            </div>
            <div className="field">
              <div className="flex align-items-center gap-2">
                <Controller
                  name="isOnMedicaid"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Checkbox
                      inputId={field.name}
                      checked={!!field.value}
                      onChange={(e) => field.onChange(!!e.checked)}
                      className={classNames({ 'p-invalid': fieldState.invalid })}
                    />
                  )}
                />
                <label htmlFor="isOnMedicaid" style={{ fontWeight: 600, color: "#0f172a" }}>
                  Resident is on Medicaid
                </label>
              </div>
              <small className="p-text-secondary">
                You can update this later from the resident profile.
              </small>
            </div>
          </div>
        </form>
      </Dialog>
      <Dialog
        visible={showResidentUnlockDialog}
        modal
        header={renderSurfaceHeader("More Resident Access Needed", "Choose how you want to continue adding residents.", "pi pi-users")}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowResidentUnlockDialog(false)}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div style={{ color: "#596F80", lineHeight: 1.7 }}>
            Your active resident limit is {currentResidentLimit > 0 ? currentResidentLimit : 1}. Archive a resident or update Billing to add more active residents.
          </div>
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              label="Close"
              severity="secondary"
              text
              rounded
              onClick={() => setShowResidentUnlockDialog(false)}
            />
            <Button
              type="button"
              label="Billing"
              severity="secondary"
              outlined
              rounded
              onClick={() => {
                setShowResidentUnlockDialog(false);
                toggleModal(false);
                navigate(`${RouterAddress.BILLING}?tab=subscription`);
              }}
            />
          </div>
        </div>
      </Dialog>
      <Dialog
        visible={showNextStepDialog}
        modal
        header={renderSurfaceHeader("Resident Added", "", "pi pi-directions-right")}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => setShowNextStepDialog(false)}
      >
        <div style={{ display: "grid", gap: "0.75rem" }}>
          <div style={{ ...appFormSectionStyle, padding: "0.9rem" }}>
            <div style={{ fontWeight: 700, color: "#0f172a" }}>Set Up Resident Profile</div>
            <div className="mt-1" style={{ ...appMutedTextStyle, lineHeight: 1.45 }}>
              Upload the latest assessment or Care Plan to get started.
            </div>
            <div className="mt-2" style={{ ...appMutedTextStyle, lineHeight: 1.45 }}>
              After processing, you can set up eMAR, ADL Tracking, Behavior Tracking, and other resident features.
            </div>
            <div className="mt-3">
              <Button
                type="button"
                label="Set Up Resident Profile"
                severity="warning"
                rounded
                data-tour="provider-next-step-upload-assessment"
                onClick={() => {
                  setShowNextStepDialog(false);
                  if (isStepActive("resident-next-step")) {
                    completeStep("resident-next-step");
                  }
                  if (createdResident?.residentId && createdResident?.homeId) {
                    navigate(RouterAddress.getResidentCarePlanPagePathForFacility(createdResident.homeId, createdResident.residentId, facilityType || undefined), {
                      state: { openAssessmentToNcp: true, profileSetup: true },
                    });
                  }
                }}
              />
            </div>
          </div>

          <div className="flex justify-content-end">
            <Button
              type="button"
              label="Do This Later"
              severity="secondary"
              outlined
              text
              rounded
              onClick={() => setShowNextStepDialog(false)}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default AddResidentModal;
